import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PremiumSidebar from './PremiumSidebar';
import baseURL from "../component/baseURL";
import showNotification from '../component/Notification';
import stringsoflanguages from '../component/stringsoflanguages';

const PREMIUM_SIDEBAR_PATHS = {
    "Premium Members": "/premium-home/premium-members",
    "Consumer Apps Active": "/premium-home/consumer-apps-active",
    "Consumer App Settings": "/premium-home/consumer-app-settings",
    "Info Requests": "/premium-home/info-requests",
    "Custom App Store": "/premium-home/custom-app-store",
};

export default function MemberCreateFilter100() {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeItem] = useState(() => location.state?.premiumSidebarActive || 'Premium Members');
    const [isMobilePageOpen, setIsMobilePageOpen] = useState(false);

    const comingFrom = location.state?.comingFrom ?? 'Create';
    const editUserPreferenceId = location.state?.user_preference_id ?? '';
    const [userID, setUserID] = useState('');
    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [userCountryId, setUserCountryId] = useState('');
    const [userStateIds, setUserStateIds] = useState([]);
    const [userCityIds, setUserCityIds] = useState([]);
    const [prefillStateNames, setPrefillStateNames] = useState([]);
    const [prefillCityNames, setPrefillCityNames] = useState([]);
    const [userCountry, setUserCountry] = useState('');
    const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
    const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
    const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const stateDropdownRef = useRef(null);
    const cityDropdownRef = useRef(null);
    const countryDropdownRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (window.innerWidth <= 767) {
            setIsMobilePageOpen(true);
        }
    }, []);

    useEffect(() => {
        document.body.classList.add('premium-page');
        return () => {
            document.body.classList.remove('premium-page');
        };
    }, []);

    const filterListPath = '/premium-home/member-filter-100';
    const premiumFilterNavState = { premiumSidebarActive: 'Premium Members' };

    const navigateBackToFilterList = () => {
        navigate(filterListPath, { state: premiumFilterNavState });
    };

    const handlePremiumSidebarNavigate = (item) => {
        window.dispatchEvent(
            new CustomEvent("premiumMembersView", {
                detail: { isViewing: item === "Premium Members" },
            })
        );
        const path = PREMIUM_SIDEBAR_PATHS[item] || "/premium-home/premium-members";
        navigate(path);
    };

    const handleMobilePremiumBack = () => {
        navigateBackToFilterList();
    };

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        const itemData = location.state?.itemData ?? null;
        setUserID(userid);
        if (itemData) {
            const countryId = itemData.country_id ?? itemData.country_code ?? itemData.country ?? '';
            const countryName = itemData.country_name ?? itemData.country ?? '';
            const stateNamesRaw = itemData.states ?? itemData.state_name ?? itemData.region_name ?? itemData.state ?? itemData.region ?? '';
            const cityNamesRaw = itemData.cities ?? itemData.city_name ?? itemData.city_label ?? '';
            const initialStateNames = String(stateNamesRaw)
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean);
            const initialCityNames = String(cityNamesRaw)
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean);

            setUserCountryId(countryId);
            setUserCountry(countryName);
            setPrefillStateNames(initialStateNames);
            setPrefillCityNames(initialCityNames);
        }
        fetchCountryAPI();
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (stateDropdownRef.current && !stateDropdownRef.current.contains(e.target)) {
                setStateDropdownOpen(false);
            }
            if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
                setCityDropdownOpen(false);
            }
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
                setCountryDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const selectedStateLabel = useMemo(() => {
        if (!userStateIds.length) return '';
        return stateList
            .filter((s) => userStateIds.includes(String(s.region_code ?? s.state_id ?? s.id ?? '')))
            .map((s) => s.region_name ?? s.state_name ?? s.name ?? '')
            .filter(Boolean)
            .join(', ');
    }, [stateList, userStateIds]);

    const selectedCityLabel = useMemo(() => {
        if (!userCityIds.length) return '';
        return cityList
            .filter((c) => userCityIds.includes(String(c.city_id ?? c.id ?? '')))
            .map((c) => c.city_name ?? c.name ?? '')
            .filter(Boolean)
            .join(', ');
    }, [cityList, userCityIds]);

    const toggleStateSelection = (id) => {
        const nextId = String(id);
        setUserStateIds((prev) => {
            const nextSelectedStateIds = prev.includes(nextId)
                ? prev.filter((v) => v !== nextId)
                : [...prev, nextId];

            setUserCityIds([]);
            setPrefillCityNames([]);
            if (userCountryId && nextSelectedStateIds.length) {
                fetchCityAPI(nextSelectedStateIds, userCountryId);
            } else {
                setCityList([]);
            }
            return nextSelectedStateIds;
        });
    };

    const toggleCitySelection = (id) => {
        const nextId = String(id);
        setUserCityIds((prev) => (
            prev.includes(nextId) ? prev.filter((v) => v !== nextId) : [...prev, nextId]
        ));
    };

    const handleCancel = () => navigateBackToFilterList();
    const handleSave = () => {
        navigateBackToFilterList();
    };


    const normalizeList = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (payload && typeof payload === 'object') return Object.values(payload);
        return [];
    };

    function fetchCountryAPI() {
        fetch(baseURL.URL + '/address/country', { method: 'POST' })
            .then((response) => response.json())
            .then((responseJson) => {
                setCountryList(responseJson.response?.countries || []);
            })
            .catch((error) => {
                console.error('Error fetching countries:', error);
                setCountryList([]);
            });
    }

    function fetchStateAPI(countryCode) {
        if (!countryCode) {
            setStateList([]);
            return;
        }
        const formdata = new FormData();
        formdata.append("country_code", countryCode);
        fetch(baseURL.URL + '/address/state', { method: 'POST', body: formdata })
            .then((response) => response.json())
            .then((responseJson) => {
                const statesPayload = responseJson.response?.states ?? responseJson.response?.regions ?? responseJson.response;
                setStateList(normalizeList(statesPayload));
            })
            .catch((error) => {
                console.error('Error fetching states:', error);
                setStateList([]);
            });
    }

    function fetchCityAPI(stateCodes, countryCode) {
        if (!stateCodes?.length || !countryCode) {
            setCityList([]);
            return;
        }
        const formdata = new FormData();
        formdata.append("country_code", countryCode);
        formdata.append("state_id", stateCodes.join(','));
        fetch(baseURL.URL + '/address/city', { method: 'POST', body: formdata })
            .then((response) => response.json())
            .then((responseJson) => {
                const citiesPayload = responseJson.response?.cities ?? responseJson.response;
                const nextCityList = normalizeList(citiesPayload);
                setCityList(nextCityList);
                if (prefillCityNames.length) {
                    const matchedCityIds = nextCityList
                        .filter((c) => prefillCityNames.includes((c.city_name ?? c.name ?? '').trim()))
                        .map((c) => String(c.city_id ?? c.id ?? ''))
                        .filter(Boolean);
                    if (matchedCityIds.length) setUserCityIds(matchedCityIds);
                    setPrefillCityNames([]);
                }
            })
            .catch((error) => {
                console.error('Error fetching cities:', error);
                setCityList([]);
            });
    }

    useEffect(() => {
        if (userCountryId) {
            fetchStateAPI(userCountryId);
        } else {
            setStateList([]);
        }
    }, [userCountryId]);

    useEffect(() => {
        if (!stateList.length || !prefillStateNames.length) return;
        const matchedStateIds = stateList
            .filter((s) => prefillStateNames.includes((s.region_name ?? s.state_name ?? s.name ?? '').trim()))
            .map((s) => String(s.region_code ?? s.state_id ?? s.id ?? ''))
            .filter(Boolean);
        if (matchedStateIds.length) {
            setUserStateIds(matchedStateIds);
            if (userCountryId) {
                fetchCityAPI(matchedStateIds, userCountryId);
            }
        } else {
            setCityList([]);
            setUserCityIds([]);
        }
        setPrefillStateNames([]);
    }, [stateList, prefillStateNames, userCountryId]);


    function createFilterApi() {
        if (userCountryId !== '') {
            console.log("Selected states: " + userStateIds.join(','));
            console.log("Selected cities: " + userCityIds.join(','));
            let formdata = new FormData();
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
            formdata.append("user_id", userID);     //c81e728d9d4c2f636f067f89cc14862c
            formdata.append("country", userCountryId);
            formdata.append("region", userStateIds.join(','));
            formdata.append("city", userCityIds.join(','));
            formdata.append("filter_type", 'pool');
            fetch(baseURL.URL + '/member/hundredp_filter_add', { method: 'POST', body: formdata })
                .then((response) => response.json())
                .then((responseJson) => {
                    console.log(responseJson);
                    navigateBackToFilterList();
                    showNotification('Success', 'Filter created successfully', 'success');
                })
                .catch((error) => {
                    console.error(error);
                    showNotification('Error', 'Failed to create filter', 'danger');
                });
        } else {
            navigateBackToFilterList();
            showNotification('Error', 'Failed to create filter', 'danger');
        }
    }


    function updateFilterApi() {
        if (userCountryId !== '') {
            let formdata = new FormData();
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
            formdata.append("user_id", userID);     //c81e728d9d4c2f636f067f89cc14862c
            formdata.append("user_pref_id", editUserPreferenceId);
            formdata.append("country", userCountryId);
            formdata.append("region", userStateIds.join(','));
            formdata.append("city", userCityIds.join(','));
            fetch(baseURL.URL + '/member/hundredp_filter_update', { method: 'POST', body: formdata })
                .then((response) => response.json())
                .then((responseJson) => {
                    console.log(responseJson);
                    navigateBackToFilterList();
                    showNotification('Success', 'Filter updated successfully', 'success');
                })
                .catch((error) => {
                    console.error(error);
                    showNotification('Error', 'Failed to update filter', 'danger');
                });
        } else {
            navigateBackToFilterList();
            showNotification('Error', 'Failed to update filter', 'danger');
        }
    }

    const mobilePageTitle =
        comingFrom === 'Edit'
            ? stringsoflanguages.editMemberFilter ?? stringsoflanguages.filter
            : stringsoflanguages.createMemberFilter ?? stringsoflanguages.filter;

    const pageBody = (
        <div className="content-right member-create-filter-page">
            <div className="member-create-filter-card">
                <h3 className="member-create-filter-title">
                    {comingFrom === 'Edit' ? stringsoflanguages.editMemberFilter : stringsoflanguages.createMemberFilter}
                </h3>
                {/* {comingFrom === 'Edit' && (
                    <p className="member-create-filter-subtitle">{stringsoflanguages.filterID}: {editUserPreferenceId}</p>
                )} */}

                <div className="member-create-filter-field">
                    <label>{stringsoflanguages.country}</label>
                    <div className="member-multi-dropdown" ref={countryDropdownRef}>
                        <button
                            type="button"
                            className="member-multi-dropdown-trigger"
                            onClick={() => setCountryDropdownOpen((prev) => !prev)}
                            aria-label="Select country"
                        >
                            {userCountry || 'Select Country'}
                            <i className="fa-solid fa-chevron-down"></i>
                        </button>
                        {countryDropdownOpen && (
                            <div className="member-multi-dropdown-menu">
                                {countryList.map((c) => {
                                    const optionId = String(c.code ?? c.id ?? '');
                                    const label = c.name ?? c.country_name ?? '';
                                    const selected = optionId && String(userCountryId ?? '') === optionId;
                                    return (
                                        <button
                                            key={optionId || label}
                                            type="button"
                                            className={`member-single-dropdown-option${selected ? ' is-selected' : ''}`}
                                            onClick={() => {
                                                setUserCountryId(optionId);
                                                setUserCountry(label);
                                                setCountryDropdownOpen(false);
                                                setUserStateIds([]);
                                                setUserCityIds([]);
                                                setPrefillStateNames([]);
                                                setPrefillCityNames([]);
                                                setStateList([]);
                                                setCityList([]);
                                                if (optionId) fetchStateAPI(optionId);
                                            }}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="member-create-filter-field">
                    <label>{stringsoflanguages.state}</label>
                    <div className="member-multi-dropdown" ref={stateDropdownRef}>
                        <button
                            type="button"
                            className="member-multi-dropdown-trigger"
                            disabled={!userCountryId}
                            onClick={() => setStateDropdownOpen((prev) => !prev)}
                        >
                            {selectedStateLabel || 'Select State(s)'}
                            <i className="fa-solid fa-chevron-down"></i>
                        </button>
                        {stateDropdownOpen && (
                            <div className="member-multi-dropdown-menu">
                                {stateList.map((s) => {
                                    const optionId = String(s.region_code ?? s.state_id ?? s.id ?? '');
                                    const label = s.region_name ?? s.state_name ?? s.name ?? '';
                                    const checked = userStateIds.includes(optionId);
                                    return (
                                        <label key={optionId} className="member-multi-dropdown-option">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleStateSelection(optionId)}
                                            />
                                            <span>{label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {/* <small className="member-create-filter-help">
                        {selectedStateLabel ? `${stringsoflanguages.selected}: ${selectedStateLabel}` : stringsoflanguages.youCanSelectMultiple}
                    </small> */}
                </div>

                <div className="member-create-filter-field">
                    <label>{stringsoflanguages.city}</label>
                    <div className="member-multi-dropdown" ref={cityDropdownRef}>
                        <button
                            type="button"
                            className="member-multi-dropdown-trigger"
                            disabled={!userStateIds.length}
                            onClick={() => setCityDropdownOpen((prev) => !prev)}
                        >
                            {selectedCityLabel || 'Select City(s)'}
                            <i className="fa-solid fa-chevron-down"></i>
                        </button>
                        {cityDropdownOpen && (
                            <div className="member-multi-dropdown-menu">
                                {cityList.map((c) => {
                                    const optionId = String(c.city_id ?? c.id ?? '');
                                    const label = c.city_name ?? c.name ?? '';
                                    const checked = userCityIds.includes(optionId);
                                    return (
                                        <label key={optionId} className="member-multi-dropdown-option">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleCitySelection(optionId)}
                                            />
                                            <span>{label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {/* <small className="member-create-filter-help">
                        {selectedCityLabel ? `${stringsoflanguages.selected}: ${selectedCityLabel}` : stringsoflanguages.youCanSelectMultiple}
                    </small> */}
                </div>

                <div className="member-create-filter-actions">
                    <button type="button" className="member-create-filter-cancel" onClick={handleCancel}>{stringsoflanguages.cancel}</button>
                    <button type="button" className="member-create-filter-save" onClick={() => comingFrom === 'Create' ? createFilterApi() : updateFilterApi()}>{stringsoflanguages.save}</button>
                </div>
            </div>
        </div>
    );

    return (
        <section className="home">
            <div className="container">
                <div className="content-holder">
                    <PremiumSidebar activeItem={activeItem} setActiveItem={handlePremiumSidebarNavigate} />
                    <div className={`content-right-wrapper ${isMobilePageOpen ? 'mobile-page-open' : ''}`}>
                        {isMobile && isMobilePageOpen && (
                            <div className="mobile-page-header">
                                <button type="button" className="mobile-back-btn" onClick={handleMobilePremiumBack}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M15 18L9 12L15 6"
                                            stroke="white"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    Back
                                </button>
                                <h2 className="mobile-page-title">{mobilePageTitle}</h2>
                            </div>
                        )}
                        {pageBody}
                    </div>
                </div>
            </div>
        </section>
    );
}
